import {
  BorderWeight,
  CalculatedKPIValues,
  CellFormat,
  CodeOrDescription,
  DecklineKind,
} from '@services/api/deckManagement/graphql/schema/API';
import { z } from 'zod';

import {
  BaseDeckManagementEntry,
  CalcExpressionInputSchema,
  CommentSchema,
  createChangeSetOperations,
  GalaxyKPIDriversInputSchema,
  nullableBoolean,
  nullableNumber,
  nullableNumberArray,
  nullableString,
} from './Common.schema';

/**
 * Schema for Deckline entries.
 * Extends BaseDeckManagementEntry to ensure consistent pk/sk handling.
 * Base schema for DeckLine that matches the API type structure.
 */
export const DecklineSchema = BaseDeckManagementEntry.extend({
  // TODO: Currently using defined basic types. Change these into more specific validations in the future.

  page_id: z.string(),
  deckline_id: z.string(),
  page_name: nullableString,
  kpi_id: nullableString,
  kpi_name: z.string().nullish(), // `nullableString` performs .trim() - we need to preserve blank space here.
  kpi_formula: nullableString,
  // `calc_members` is the authored calculated-KPI expression. The backend derives `kpi_id` /
  // `kpi_formula` / `is_calculated_kpi` from it (one per column), so for a calc row the FE sends this
  // and nulls the baked id/formula. REQUIRED in the schema because zod's default STRIP would otherwise
  // drop it silently from the save payload with no error. `.nullish()` so non-calc rows omit it.
  // See `design-docs/page-editor/calculated-kpis.md` (Backend integration).
  calc_members: CalcExpressionInputSchema,
  serial_no: nullableString,
  is_code_or_description: z.enum(CodeOrDescription).nullish(),
  is_format_bold: nullableBoolean,
  is_format_italics: nullableBoolean,
  is_format_underline: nullableBoolean,
  format_text_color: nullableString,
  format_cell_color: nullableString,
  format_cell_default: z.enum(CellFormat).nullish(),
  // `blankline_type` is not required in this save schema - handled by the backend.
  is_format_borderline: nullableBoolean,
  footnotes: nullableNumberArray,
  // `subtotal_formula` is intentionally NOT in the save schema: an additive subtotal's formula is
  // derived by the backend from its membership (`subtotal_parent` + `serial_no`) and dropped on save
  // (confirmed with the BE team — no subtotal rows in production; safe to stop sending). It stays on
  // the local `Deckline`/`EnhancedDeckline` model so `determineDecklineType` still classifies the row
  // as a SUBTOTAL; Zod's default strip drops it from the save payload. Only the membership persists.
  subtotal_parent: nullableString,
  // `deckline_kind` is the row-kind sent on save; the backend derives `is_calculated_kpi` and `kpi_type`
  // from it, so the FE no longer authors `is_calculated_kpi` here. It is stamped at save-prep from the
  // row's resolved type (see DecklinesContext), so it is always a valid enum member — never null — and
  // the backend's `normalize_for_kind` fills any silent fields / rejects contradictions.
  deckline_kind: z.enum(DecklineKind).nullish(),
  // `deckline_drivers` is intentionally NOT in the save schema: for base rows the backend derives it at
  // read (from the linked variant's `variant_overrides`) and drops it on save, so the FE must never
  // persist it. It stays on the local `Deckline`/`EnhancedDeckline` model for display + in-session edits;
  // Zod's default strip drops any leftover value from the save payload here. (Same treatment will apply
  // to `denominator_kpi_id_drivers` when that FK migration lands.)
  is_exception: nullableBoolean,
  page_section: nullableString,
  adjcalcflag: nullableBoolean,
  exceptioncalculatedkpiflag: nullableBoolean,
  bordertopflag: nullableBoolean,
  borderrightflag: nullableBoolean,
  borderbottomflag: nullableBoolean,
  borderleftflag: nullableBoolean,
  borderweightflag: z.enum(BorderWeight).nullish(),
  textunderlineflag: nullableBoolean,
  denominator_kpi_id_for_pct: nullableString,
  denominator_kpi_id_drivers: GalaxyKPIDriversInputSchema,
  denominator_is_calculated_kpi: z.enum(CalculatedKPIValues).nullish(),
  denominator_kpi_formula: nullableString,
  root_kpi_id: nullableString,
  kpi_variant_id: nullableString,
  display_name: nullableString,
  denominator_kpi_variant_id: nullableString,
  kpi_name_indent_level: nullableNumber,
  comments: z.array(CommentSchema).nullish(),
});

/**
 * Schema for creating new DeckLine entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DecklineItemsInput GraphQL type requirements.
 */
export const CreateDecklineSchema = DecklineSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for updating existing Deckline entries.
 * Omits pk and sk fields as these are generated by the service.
 * Matches DecklineItemsInput GraphQL type requirements.
 */
export const UpdateDecklineSchema = DecklineSchema.omit({
  pk: true,
  sk: true,
});

/**
 * Schema for deleting existing Deckline entries
 * Includes only the primary fields needed for DecklineDeleteItemsInput
 */
export const DeleteDecklineSchema = z.object({
  page_id: z.string(),
  deckline_id: z.string(),
});

/**
 * Complete changeset schema for Deckline operations.
 * Provides create, update, and delete arrays with appropriate validation.
 */
export const DecklineChangeSetSchema = createChangeSetOperations({
  create: CreateDecklineSchema,
  update: UpdateDecklineSchema,
  delete: DeleteDecklineSchema,
});

/**
 * Type definitions for use in components and services
 */

/**
 * Type for Deckline entries in create operations (without pk/sk)
 */
export type CreateDecklineType = z.infer<typeof CreateDecklineSchema>;

/**
 * Type for Deckline entries in udpate operation (without pk/sk, or last_updated_date)
 */
export type UpdateDecklineType = z.infer<typeof UpdateDecklineSchema>;

/**
 * Type for Deckline entries in delete operations (with all fields)
 */
export type DeleteDecklineType = z.infer<typeof DeleteDecklineSchema>;

/**
 * Type for the complete Deckline changeset structure
 */
export type DecklineChangeSetType = z.infer<typeof DecklineChangeSetSchema>;
